import type { CSSProperties, FC, ReactNode } from 'react'
import type { Stat, Tone } from '@/types'

import { cn } from '@/lib/utils'
import { Counter } from '@/components/generals/counter'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'

interface StatGridProps {
    stats: Stat[]
    index?: string
    eyebrow?: string
    title: string
    intro?: string
    tone?: Tone
    id?: string
}

const DARK: Tone[] = ['night', 'deep', 'raised']

export const StatGrid: FC<StatGridProps> = ({
    stats,
    index,
    eyebrow = 'Tallene',
    title,
    intro,
    tone = 'paper',
    id = 'tallene'
}): ReactNode => (
    <section
        id={id}
        className={cn(`tone-${tone}`, DARK.includes(tone) && 'grain', 'relative overflow-hidden py-24 md:py-36')}
        data-header={DARK.includes(tone) ? 'dark' : 'light'}
    >
        <div className='wrap'>
            <div className='grid grid-cols-12 gap-x-6 gap-y-8'>
                <div className='col-span-12 lg:col-span-7'>
                    <SectionLabel index={index}>{eyebrow}</SectionLabel>
                    <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                        <SplitWords text={title} />
                    </h2>
                </div>

                {intro && (
                    <p className='col-span-12 self-end text-tone-muted t-lead pretty lg:col-span-4 lg:col-start-9'>
                        {intro}
                    </p>
                )}
            </div>

            <div className='mt-16 grid grid-cols-1 border-t border-tone-line md:mt-24 md:grid-cols-3'>
                {stats.map((stat, i) => (
                    <div
                        key={stat.label}
                        className={cn(
                            'flex flex-col border-b border-tone-line py-10 md:border-b-0 md:px-8 md:py-12',
                            i > 0 && 'md:border-l',
                            i === 0 && 'md:pl-0'
                        )}
                        data-reveal='rise'
                        style={{ '--d': i } as CSSProperties}
                    >
                        <p className='t-label text-tone-muted'>{stat.label}</p>

                        <p className='mt-8 flex items-baseline gap-3'>
                            <Counter value={stat.value} className='t-display wd-88' />
                            {stat.unit && <span className='t-h3 text-tone-muted'>{stat.unit}</span>}
                        </p>

                        <p className='mt-6 max-w-xs text-[1.0625rem] leading-snug pretty'>{stat.text}</p>

                        {stat.note && <p className='mt-auto pt-8 text-xs text-tone-muted'>{stat.note}</p>}
                    </div>
                ))}
            </div>
        </div>
    </section>
)
